if (!customElements.get('seo-blog-search')) {
  customElements.define(
    'seo-blog-search',
    class SeoBlogSearch extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.form = this.querySelector('.seo-blog-search__form');
        this.input = this.querySelector('.seo-blog-search__input');
        this.clearButton = this.querySelector('.seo-blog-search__clear');
        this.results = this.querySelector('[data-search-results]');
        this.resultsTitle = this.querySelector('[data-search-results-title]');
        this.resultsCount = this.querySelector('[data-search-results-count]');
        this.resultsList = this.querySelector('[data-search-results-list]');
        this.status = this.querySelector('[data-search-status]');

        if (!this.form || !this.input || !this.results || !this.resultsList) return;

        this.minimumLength = Number.parseInt(this.dataset.minLength, 10) || 1;
        this.resultLimit = Number.parseInt(this.dataset.resultLimit, 10) || 6;
        this.blogPath = this.normalizePath(this.dataset.blogPath || '');
        this.cache = new Map();
        this.abortController = null;
        this.debounceTimer = null;

        this.onInput = this.handleInput.bind(this);
        this.onSubmit = this.handleSubmit.bind(this);
        this.onClear = this.clear.bind(this);
        this.onKeydown = this.handleKeydown.bind(this);
        this.onDocumentClick = this.handleDocumentClick.bind(this);
        this.onFocus = this.handleFocus.bind(this);

        this.input.addEventListener('input', this.onInput);
        this.input.addEventListener('keydown', this.onKeydown);
        this.input.addEventListener('focus', this.onFocus);
        this.form.addEventListener('submit', this.onSubmit);
        this.clearButton?.addEventListener('click', this.onClear);
        document.addEventListener('click', this.onDocumentClick);
      }

      disconnectedCallback() {
        window.clearTimeout(this.debounceTimer);
        this.abortController?.abort();
        document.removeEventListener('click', this.onDocumentClick);
      }

      normalizePath(path) {
        if (!path) return '';
        return `/${String(path).replace(/^\/+|\/+$/g, '')}`;
      }

      handleInput() {
        const query = this.input.value.trim();
        this.clearButton.hidden = query.length === 0;
        window.clearTimeout(this.debounceTimer);

        if (query.length < this.minimumLength) {
          this.abortController?.abort();
          this.setLoading(false);
          this.closeResults();
          this.status.textContent = '';
          return;
        }

        this.debounceTimer = window.setTimeout(() => this.search(query), 250);
      }

      handleSubmit(event) {
        event.preventDefault();
        window.clearTimeout(this.debounceTimer);
        const query = this.input.value.trim();
        if (query.length < this.minimumLength) {
          this.closeResults();
          return;
        }
        this.search(query);
      }

      handleKeydown(event) {
        if (event.key === 'Escape') {
          this.closeResults();
          this.input.blur();
          return;
        }

        if (event.key !== 'ArrowDown' || this.results.hidden) return;
        const firstResult = this.resultsList.querySelector('a');
        if (firstResult) {
          event.preventDefault();
          firstResult.focus();
        }
      }

      handleFocus() {
        if (this.resultsList.childElementCount > 0 && this.input.value.trim().length >= this.minimumLength) {
          this.openResults();
        }
      }

      handleDocumentClick(event) {
        if (!this.contains(event.target)) this.closeResults();
      }

      clear() {
        window.clearTimeout(this.debounceTimer);
        this.abortController?.abort();
        this.setLoading(false);
        this.input.value = '';
        this.clearButton.hidden = true;
        this.resultsList.replaceChildren();
        this.status.textContent = '';
        this.closeResults();
        this.input.focus();
      }

      async search(query) {
        const normalizedQuery = query.toLocaleLowerCase();
        if (this.cache.has(normalizedQuery)) {
          this.abortController?.abort();
          this.setLoading(false);
          this.renderResults(this.cache.get(normalizedQuery), query);
          return;
        }

        this.abortController?.abort();
        this.abortController = new AbortController();
        this.setLoading(true);

        try {
          const searchUrl = new URL(this.dataset.searchUrl || '/search/suggest', window.location.origin);
          if (!searchUrl.pathname.endsWith('.json')) searchUrl.pathname = `${searchUrl.pathname}.json`;
          searchUrl.searchParams.set('q', query);
          searchUrl.searchParams.set('resources[type]', 'article');
          searchUrl.searchParams.set('resources[limit]', '10');
          searchUrl.searchParams.set('resources[options][fields]', 'title,body,tag');
          searchUrl.searchParams.set('resources[options][prefix]', 'last');

          const response = await fetch(searchUrl.toString(), {
            headers: { Accept: 'application/json' },
            signal: this.abortController.signal,
          });
          if (!response.ok) throw new Error(`Article search failed with status ${response.status}`);

          const payload = await response.json();
          const articles = payload?.resources?.results?.articles || [];
          const filteredArticles = articles
            .filter((article) => {
              const articlePath = this.normalizePath(new URL(article.url, window.location.origin).pathname);
              return !this.blogPath || articlePath.startsWith(`${this.blogPath}/`);
            })
            .slice(0, this.resultLimit);

          this.cache.set(normalizedQuery, filteredArticles);
          if (this.input.value.trim() === query) this.renderResults(filteredArticles, query);
        } catch (error) {
          if (error.name !== 'AbortError' && this.input.value.trim() === query) this.renderError();
        } finally {
          if (this.input.value.trim() === query) this.setLoading(false);
        }
      }

      renderResults(articles, query) {
        this.resultsList.replaceChildren();
        this.resultsTitle.textContent = this.dataset.resultsTitle || '';
        this.resultsCount.textContent = this.formatTemplate(this.dataset.resultsCountTemplate, {
          count: articles.length,
          query,
        });

        if (articles.length === 0) {
          this.resultsList.append(this.createMessage(this.dataset.emptyMessage));
        } else {
          const fragment = document.createDocumentFragment();
          articles.forEach((article) => fragment.append(this.createResult(article)));
          this.resultsList.append(fragment);
        }

        this.status.textContent = this.resultsCount.textContent;
        this.openResults();
      }

      createResult(article) {
        const item = document.createElement('article');
        item.className = 'seo-blog-search-result';
        item.setAttribute('role', 'listitem');

        const link = document.createElement('a');
        link.className = 'seo-blog-search-result__link';
        link.href = article.url;

        const imageUrl = article.featured_image?.url || article.image;
        if (imageUrl) {
          const media = document.createElement('span');
          media.className = 'seo-blog-search-result__media';
          const image = document.createElement('img');
          image.src = imageUrl;
          image.alt = article.featured_image?.alt || '';
          image.loading = 'lazy';
          image.width = 160;
          image.height = 96;
          media.append(image);
          link.append(media);
        } else {
          item.classList.add('seo-blog-search-result--no-image');
        }

        const content = document.createElement('span');
        content.className = 'seo-blog-search-result__content';
        const title = document.createElement('span');
        title.className = 'seo-blog-search-result__title';
        title.textContent = article.title;
        content.append(title);

        const excerptText = this.stripHtml(article.summary_html || article.body || '');
        if (excerptText) {
          const excerpt = document.createElement('span');
          excerpt.className = 'seo-blog-search-result__excerpt';
          excerpt.textContent = this.truncate(excerptText, 150);
          content.append(excerpt);
        }

        if (article.published_at) {
          const date = document.createElement('time');
          date.className = 'seo-blog-search-result__date';
          date.dateTime = article.published_at.slice(0, 10);
          date.textContent = new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          }).format(new Date(article.published_at));
          content.append(date);
        }

        link.append(content);
        item.append(link);
        return item;
      }

      createMessage(message) {
        const element = document.createElement('p');
        element.className = 'seo-blog-search__message';
        element.textContent = message || '';
        return element;
      }

      renderError() {
        this.resultsList.replaceChildren(this.createMessage(this.dataset.errorMessage));
        this.resultsTitle.textContent = this.dataset.resultsTitle || '';
        this.resultsCount.textContent = '';
        this.status.textContent = this.dataset.errorMessage || '';
        this.openResults();
      }

      setLoading(isLoading) {
        this.toggleAttribute('loading', isLoading);
        if (isLoading) this.status.textContent = this.dataset.loadingMessage || '';
      }

      openResults() {
        this.results.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
      }

      closeResults() {
        this.results.hidden = true;
        this.input.setAttribute('aria-expanded', 'false');
      }

      stripHtml(html) {
        return new DOMParser().parseFromString(html, 'text/html').body.textContent.trim();
      }

      truncate(text, maximumLength) {
        if (text.length <= maximumLength) return text;
        return `${text.slice(0, maximumLength).trimEnd()}\u2026`;
      }

      formatTemplate(template = '', values = {}) {
        return Object.entries(values).reduce(
          (output, [key, value]) => output.replaceAll(`:${key}`, String(value)),
          template,
        );
      }
    },
  );
}
